// ============================================================
//  ALTIMO Medical Center — Usuarios.jsx
//  Gestión de usuarios: CRUD, roles, activar/desactivar,
//  reseteo de contraseña, monitoreo de actividad
//  Solo accesible por rol: admin
//  Ubicación: src/components/Usuarios.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';

// ── Roles disponibles en el sistema ─────────────────────────
const ROLES = [
  { value: 'admin',              label: 'Administrador',          color: '#155724', bg: '#d4edda', desc: 'Acceso total al sistema' },
  { value: 'admision',          label: 'Admisión',                color: '#856404', bg: '#fff3cd', desc: 'Registro de pacientes e ingreso' },
  { value: 'medico',            label: 'Médico',                  color: '#4a2080', bg: '#e2d9f3', desc: 'Expedientes, evoluciones, órdenes' },
  { value: 'jefe_medico',       label: 'Jefe Médico de Turno',    color: '#1B2A5E', bg: '#c8d8f8', desc: 'Médico a cargo del turno — acceso total a pacientes, expedientes y formularios' },
  { value: 'enfermeria',        label: 'Enfermería',              color: '#0c5460', bg: '#d1ecf1', desc: 'Signos vitales, formularios clínicos' },
  { value: 'jefa_enfermeria',   label: 'Jefa de Enfermería',      color: '#064e58', bg: '#a8dbe5', desc: 'Acceso completo a enfermería, expedientes y observaciones de pacientes' },
  { value: 'supervisora_turno', label: 'Supervisora de Turno',    color: '#5a3a6d', bg: '#e0d0f0', desc: 'Supervisión del turno — acceso a expedientes y formularios, trazabilidad completa' },
  { value: 'caja',              label: 'Caja / Facturación',      color: '#004085', bg: '#cce5ff', desc: 'Facturación, reportes, cobros' },
  { value: 'laboratorio',       label: 'Laboratorio',             color: '#72243E', bg: '#f4c0d1', desc: 'Carga de resultados de laboratorio' },
  { value: 'farmacia',          label: 'Farmacia / Botiquín',     color: '#3B6D11', bg: '#d4edda', desc: 'Inventario y dispensación' },
  { value: 'recepcion',         label: 'Recepción',               color: '#1B2A5E', bg: '#e8f0fb', desc: 'Atención al paciente, consultas' },
  { value: 'contaduria',        label: 'Contaduría',              color: '#7a4f01', bg: '#ffe8b3', desc: 'Reportes financieros, caja diaria, libro de ventas e IVA' },
  { value: 'auditoria',         label: 'Auditoría',               color: '#4a4a4a', bg: '#e2e2e2', desc: 'Solo lectura — reportes financieros y trazabilidad' },
];

// ── Estilos ──────────────────────────────────────────────────
const S = {
  pagina:  { background: '#f0f4f8', minHeight: '100vh', padding: '20px', fontFamily: 'Segoe UI, sans-serif' },
  card:    { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '18px', marginBottom: '16px' },
  titulo:  { fontSize: '12px', fontWeight: '700', color: '#1B2A5E', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  input:   { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box' },
  label:   { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em' },
  campo:   { marginBottom: '13px' },
  fila2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fila3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  tabla:   { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:      { textAlign: 'left', padding: '8px 12px', fontSize: '11px', color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd' },
  td:      { padding: '10px 12px', borderBottom: '1px solid #eef1f6', color: '#333', verticalAlign: 'middle' },
  btnVerde:  { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnAzul:   { background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnNaranja:{ background: '#e67e22', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnRojo:   { background: '#dc3545', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnGris:   { background: '#6c757d', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnS:      { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '7px 12px', fontSize: '12px', cursor: 'pointer' },
  alerta:  (t) => ({
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '12px',
    whiteSpace: 'pre-line',
    background: t === 'ok' ? '#d4edda' : t === 'err' ? '#f8d7da' : '#fff3cd',
    color:      t === 'ok' ? '#155724' : t === 'err' ? '#721c24' : '#856404',
    border:    `1px solid ${t === 'ok' ? '#c3e6cb' : t === 'err' ? '#f5c6cb' : '#ffc107'}`,
  }),
  modal:     { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#fff', borderRadius: '12px', padding: '28px', width: '480px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #dde3ea', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitulo: { fontSize: '16px', fontWeight: '800', color: '#1B2A5E', marginBottom: '20px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '16px' },
  kpiCard: { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '14px 16px' },
  kpiLabel:{ fontSize: '10px', color: '#6c757d', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '6px' },
  kpiVal:  { fontSize: '24px', fontWeight: '800', color: '#1B2A5E' },
  kpiSub:  { fontSize: '11px', color: '#adb5bd', marginTop: '3px' },
  avatar:  (color) => ({ width: '36px', height: '36px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px', fontWeight: '700', flexShrink: 0 }),
  pillRol: (rol) => {
    const r = ROLES.find(x => x.value === rol) || { color: '#383d41', bg: '#e2e3e5', label: rol };
    return { display: 'inline-block', padding: '2px 10px', borderRadius: '99px', fontSize: '10px', fontWeight: '700', background: r.bg, color: r.color };
  },
  pillActivo: (activo) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: '99px', fontSize: '10px', fontWeight: '700', background: activo ? '#d4edda' : '#f8d7da', color: activo ? '#155724' : '#721c24' }),
  actividadRow: { padding: '8px 10px', borderBottom: '1px solid #f0f4f8', fontSize: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' },
  secH: { fontSize: '11px', fontWeight: '700', color: '#1B2A5E', textTransform: 'uppercase', letterSpacing: '.05em', margin: '14px 0 8px', paddingBottom: '4px', borderBottom: '2px solid #e8f0fb' },
};

// Helper: iniciales para avatar
const iniciales = (nombre) => nombre?.split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase() || '?';

// Helper: color del avatar por rol
const colorAvatar = (rol) => {
  const map = {
    admin:              '#1B2A5E',
    admision:           '#856404',
    medico:             '#4a2080',
    jefe_medico:        '#1B2A5E',
    enfermeria:         '#0c5460',
    jefa_enfermeria:    '#064e58',
    supervisora_turno:  '#5a3a6d',
    caja:               '#004085',
    laboratorio:        '#72243E',
    farmacia:           '#3B6D11',
    recepcion:          '#495057',
  };
  return map[rol] || '#6c757d';
};



// ── Procesamiento de firma/sello: convertir a PNG transparente ─────────────
function leerArchivoComoDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string' && reader.result.startsWith('data:')) {
        resolve(reader.result);
      } else {
        reject(new Error('No se pudo leer el archivo de imagen.'));
      }
    };
    reader.onerror = () => reject(new Error('Falló la lectura del archivo de imagen.'));
    reader.readAsDataURL(file);
  });
}

async function cargarImagenDesdeArchivo(file) {
  if (!file) throw new Error('No se recibió ningún archivo.');

  // Primer intento: createImageBitmap suele ser más tolerante en Electron/Chromium.
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file);
      return bmp;
    } catch (_) {
      // seguimos a fallback con DataURL
    }
  }

  return new Promise(async (resolve, reject) => {
    try {
      const dataUrl = await leerArchivoComoDataURL(file);
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('No se pudo decodificar la imagen seleccionada. Intenta con un PNG o JPG estándar.'));
      img.src = dataUrl;
    } catch (e) {
      reject(new Error(e?.message || 'No se pudo cargar la imagen seleccionada.'));
    }
  });
}

function canvasToBlob(canvas, type='image/png', quality=0.92) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('No se pudo convertir la imagen procesada.'));
          return;
        }
        resolve(blob);
      }, type, quality);
    } catch (e) {
      reject(new Error(e?.message || 'Falló la conversión de la imagen.'));
    }
  });
}

async function procesarFirmaSello(file) {
  if (!file) throw new Error('No se recibió ningún archivo.');
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('El archivo seleccionado no es una imagen válida.');
  }

  const img = await cargarImagenDesdeArchivo(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('El navegador no pudo preparar el procesamiento de la imagen.');
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = image.data;

  // Quitar blanco y casi blanco. También remueve grises muy claros para evitar cajas.
  const WHITE = 245;
  const LIGHT = 235;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i+1], b = data[i+2], a = data[i+3];
    if (a === 0) continue;
    const maxc = Math.max(r,g,b);
    const minc = Math.min(r,g,b);
    const diff = maxc - minc;

    const esBlanco = r >= WHITE && g >= WHITE && b >= WHITE;
    const esCasiBlanco = r >= LIGHT && g >= LIGHT && b >= LIGHT && diff <= 18;

    if (esBlanco || esCasiBlanco) {
      data[i+3] = 0;
    }
  }
  ctx.putImageData(image, 0, 0);

  // Recortar al contenido visible para que no quede una imagen enorme vacía.
  const processed = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const idx = (y * canvas.width + x) * 4;
      if (processed[idx + 3] > 10) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX === -1 || maxY === -1) {
    throw new Error('No se encontró contenido visible en la imagen. Usa una firma/sello más nítido.');
  }

  const padding = 8;
  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(canvas.width - 1, maxX + padding);
  maxY = Math.min(canvas.height - 1, maxY + padding);

  const out = document.createElement('canvas');
  const outCtx = out.getContext('2d');
  out.width = Math.max(1, maxX - minX + 1);
  out.height = Math.max(1, maxY - minY + 1);
  outCtx.drawImage(canvas, minX, minY, out.width, out.height, 0, 0, out.width, out.height);

  const blob = await canvasToBlob(out, 'image/png');

  const finalDataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = e => resolve(e.target?.result);
    r.onerror = () => reject(new Error('No se pudo leer la imagen procesada.'));
    r.readAsDataURL(blob);
  });
  if (!finalDataUrl || typeof finalDataUrl !== 'string') {
    throw new Error('No se pudo generar la vista previa de la imagen procesada.');
  }

  return { dataUrl: finalDataUrl, width: out.width, height: out.height, size: blob.size };
}

// ════════════════════════════════════════════════════════════
//  MODAL: Crear / Editar usuario
// ════════════════════════════════════════════════════════════

// Formatea DUI: convierte "########1" → "########-1"
function formatearDUI(valor) {
  const solo = String(valor || '').replace(/[^0-9]/g, '').slice(0, 9);
  if (solo.length <= 8) return solo;
  return solo.slice(0, 8) + '-' + solo.slice(8);
}

function validarDUI(dui) {
  return /^\d{8}-\d$/.test(String(dui || '').trim());
}

function ModalUsuario({ usuario_actual, editar, onGuardado, onCerrar, onFirmaSubida }) {
  const esNuevo = !editar;
  const [form,         setForm]         = useState({
    nombre:    editar?.nombre   || '',
    dui:       editar?.usuario  || '',   // Para nuevo: DUI limpio; para editar: usuario actual
    rol:       editar?.rol      || 'admision',
    titulo:    editar?.titulo   || '',
    medico_id: editar?.medico_id || '',  // Vínculo con catálogo de médicos (solo rol medico/jefe_medico)
    // Solo para edición — cambio voluntario de contraseña por admin
    password:  '',
    password2: '',
  });
  const [medicosDisponibles, setMedicosDisponibles] = useState([]);
  const [guardando,    setGuardando]    = useState(false);
  const [error,        setError]        = useState('');
  // Firma/sello
  const [firmaSelloB64, setFirmaSelloB64] = useState(null);
  const [subiendoImg,   setSubiendoImg]   = useState(false);
  const [msgFirma,      setMsgFirma]      = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Cargar imagen combinada existente al abrir modal de edición.
  // Antes: api.usuarios.leerFirma({ ruta }) leía el archivo del
  // disco de la PC y lo convertía a base64. Ahora la ruta ya es una URL
  // pública servida por el servidor (ej. "/archivos/firmas/..."), así que
  // simplemente se usa directo como src de la imagen — no hace falta
  // ninguna llamada a la API.
  const rutaActual = editar?.firma_sello_ruta || editar?.firma_ruta || null;
  useEffect(() => {
    if (!rutaActual) { setFirmaSelloB64(null); return; }
    setFirmaSelloB64(api.lab.urlArchivo(rutaActual));
  }, [rutaActual]);

  // Cargar catálogo de médicos para el selector de vínculo (solo se usa si el
  // rol es medico/jefe_medico, pero se carga una vez al abrir el modal).
  useEffect(() => {
    (async () => {
      try {
        const r = await api.medicos.listar();
        if (r.ok) setMedicosDisponibles((r.data || []).filter(m => m.activo));
      } catch (_) { /* el selector simplemente queda vacío */ }
    })();
  }, []);

  const subirImagen = () => {
    if (!editar?.id) {
      setError('Guarda el usuario primero para poder subir la imagen de firma y sello.'); return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/jpg';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) { setError('Solo se permiten imágenes.'); return; }
      if (file.size > 4 * 1024 * 1024) { setError('La imagen original no debe superar 4 MB.'); return; }

      setSubiendoImg(true); setMsgFirma(''); setError('');
      try {
        const procesada = await procesarFirmaSello(file);
        if (procesada.size > 2 * 1024 * 1024)
          throw new Error('La imagen procesada supera 2 MB. Usa una firma/sello más simple o más pequeño.');

        const base64 = procesada.dataUrl.split(',')[1];
        const r = await api.usuarios.subirFirma({
          usuario_id: editar.id,
          tipo: 'firma_sello',
          fileData: base64,
          fileExt: '.png',
        });

        if (r.ok) {
          setFirmaSelloB64(procesada.dataUrl);
          setMsgFirma(`✓ Firma y sello guardados (${procesada.width}x${procesada.height}).`);
          if (onFirmaSubida) onFirmaSubida(editar.id, r.data?.ruta || null);
        } else {
          setError('Error al subir: ' + r.error);
        }
      } catch(e) {
        setError('Error al procesar imagen: ' + e.message);
      }
      setSubiendoImg(false);
    };
    input.click();
  };

  const guardar = async () => {
    const erroresValidacion = [];

    if (!form.nombre.trim()) erroresValidacion.push('El nombre es obligatorio.');

    if (esNuevo) {
      const duiLimpio = form.dui.trim();
      if (!duiLimpio) erroresValidacion.push('El DUI es obligatorio.');
      else if (!validarDUI(duiLimpio)) erroresValidacion.push('DUI inválido. Formato: 00000000-0 (8 dígitos, guión, 1 dígito).');
    }

    // Validación de contraseña (solo edición — campo opcional)
    if (!esNuevo && form.password) {
      if (form.password !== form.password2) erroresValidacion.push('Las contraseñas no coinciden.');
      if (form.password.length < 6)          erroresValidacion.push('Mínimo 6 caracteres.');
    }

    if (erroresValidacion.length > 0) {
      setError(erroresValidacion.length === 1
        ? erroresValidacion[0]
        : `Hay ${erroresValidacion.length} campos por corregir:\n` + erroresValidacion.map(e => `• ${e}`).join('\n'));
      return;
    }

    setGuardando(true); setError('');
    try {
      let r;
      if (esNuevo) {
        r = await api.usuarios.crear({
          nombre:    form.nombre.trim(),
          dui:       form.dui.trim(),
          rol:       form.rol,
          titulo:    form.titulo.trim() || null,
          medico_id: form.medico_id || null,
        });
      } else {
        r = await api.usuarios.editar({
          id:        editar.id,
          nombre:    form.nombre.trim(),
          usuario:   editar.usuario,         // El usuario (DUI) no se modifica
          rol:       form.rol,
          titulo:    form.titulo.trim() || null,
          medico_id: form.medico_id || null,
        });
        if (form.password) {
          await api.usuarios.cambiarPassword({ id: editar.id, nuevaPassword: form.password });
        }
      }
      if (!r.ok && r.error) throw new Error(r.error);
      onGuardado(esNuevo ? '✓ Usuario creado. Contraseña inicial: BienvenidoAltimo' : '✓ Usuario actualizado.');
    } catch(e) {
      setError(e.message || 'Error al guardar.');
    }
    setGuardando(false);
  };

  const rolInfo = ROLES.find(r => r.value === form.rol);

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.modalCard, width:'560px' }} onClick={e => e.stopPropagation()}>
        <div style={S.modalTitulo}>
          {esNuevo ? '➕ Nuevo usuario' : `✏️ Editar: ${editar.nombre}`}
        </div>

        {error    && <div style={S.alerta('err')}>{error}</div>}
        {msgFirma && <div style={S.alerta('ok')}>{msgFirma}</div>}

        {/* Datos básicos */}
        <div style={S.fila2}>
          <div style={S.campo}>
            <label style={S.label}>Nombre completo *</label>
            <input style={S.input} value={form.nombre}
              onChange={e => set('nombre', e.target.value)} placeholder="Nombre y apellido" />
          </div>
          <div style={S.campo}>
            <label style={S.label}>Título profesional</label>
            <input style={S.input} value={form.titulo}
              onChange={e => set('titulo', e.target.value)}
              placeholder="Dr., Lic., Enf., DDS..." />
          </div>
        </div>

        {/* DUI (solo nuevo) / Usuario (edición — solo lectura) */}
        <div style={S.campo}>
          <label style={S.label}>
            {esNuevo ? 'DUI (será el usuario de acceso) *' : 'DUI / Usuario de acceso'}
          </label>
          {esNuevo ? (
            <>
              <input style={S.input}
                value={form.dui}
                onChange={e => set('dui', formatearDUI(e.target.value))}
                placeholder="00000000-0"
                maxLength={10}
                autoComplete="off" />
              <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'3px' }}>
                Formato: 8 dígitos, guión y dígito verificador (ej: 01234567-8)
              </div>
            </>
          ) : (
            <>
              <input style={{ ...S.input, background:'#f8fafd', color:'#6c757d' }}
                value={editar.usuario} readOnly />
              <div style={{ fontSize:'10px', color:'#adb5bd', marginTop:'3px' }}>
                El DUI/usuario no se puede modificar para proteger sesiones activas.
              </div>
            </>
          )}
        </div>

        <div style={S.campo}>
          <label style={S.label}>Rol / Área *</label>
          <select style={S.input} value={form.rol} onChange={e => set('rol', e.target.value)}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {rolInfo && (
            <div style={{ marginTop:'6px', padding:'6px 10px', background: rolInfo.bg, borderRadius:'6px', fontSize:'11px', color: rolInfo.color, fontWeight:'500' }}>
              🔐 {rolInfo.desc}
            </div>
          )}
        </div>

        {/* Vínculo con catálogo de médicos — habilita "Mi panel" en el
            Dashboard (mis pacientes, labs pendientes, recetas sin dispensar).
            Solo aplica a roles médico / jefe médico de turno. */}
        {(form.rol === 'medico' || form.rol === 'jefe_medico') && (
          <div style={S.campo}>
            <label style={S.label}>Médico vinculado (catálogo de Médicos)</label>
            <select style={S.input} value={form.medico_id} onChange={e => set('medico_id', e.target.value)}>
              <option value="">— Sin vincular —</option>
              {medicosDisponibles.map(m => (
                <option key={m.id} value={m.id}>
                  {m.titulo || 'Dr.'} {m.nombre} {m.apellido || ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'3px' }}>
              Vincula este usuario con su registro en el módulo "Médicos" para que su Dashboard muestre
              automáticamente sus pacientes activos, resultados de laboratorio y prescripciones pendientes.
              {medicosDisponibles.length === 0 && (
                <span style={{ color:'#856404' }}> No hay médicos activos en el catálogo — crea uno primero en el módulo "Médicos".</span>
              )}
            </div>
          </div>
        )}

        {/* Contraseña inicial — aviso para nuevo usuario */}
        {esNuevo && (
          <div style={{ background:'#e8f0fb', border:'1px solid #1B2A5E33', borderRadius:'10px', padding:'12px 14px', marginBottom:'14px', display:'flex', gap:'10px', alignItems:'flex-start' }}>
            <span style={{ fontSize:'20px' }}>🔑</span>
            <div>
              <div style={{ fontSize:'12px', fontWeight:'700', color:'#1B2A5E', marginBottom:'3px' }}>Contraseña inicial automática</div>
              <div style={{ fontSize:'11px', color:'#495057', lineHeight:1.5 }}>
                La contraseña temporal será <strong>BienvenidoAltimo</strong>. Al ingresar al sistema por primera vez, el usuario deberá establecer su propia contraseña personal.
              </div>
            </div>
          </div>
        )}

        {/* Firma y sello */}
        <div style={S.secH}>
          Firma y sello escaneado
          <span style={{ fontSize:'10px', fontWeight:'400', color:'#6c757d', marginLeft:'8px' }}>
            Una imagen con firma y sello — el sistema le quita el fondo y guarda como PNG transparente
          </span>
        </div>

        {esNuevo ? (
          <div style={{ background:'#fff8e1', border:'1px solid #ffe082', borderRadius:'8px', padding:'10px 14px', fontSize:'11px', color:'#795548', marginBottom:'12px' }}>
            💡 Crea el usuario primero, luego edítalo para cargar la imagen de firma y sello.
          </div>
        ) : (
          <div style={{ border:'1px solid #dde3ea', borderRadius:'10px', padding:'14px', background:'#f8fafd', marginBottom:'14px' }}>
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#1B2A5E', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'.04em' }}>
              Imagen de Firma + Sello
            </div>
            {firmaSelloB64 ? (
              <div style={{ background:'#fff', border:'1px solid #c8ddf7', borderRadius:'7px', padding:'10px', textAlign:'center', marginBottom:'10px', minHeight:'90px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <img src={firmaSelloB64} alt="Firma y Sello"
                  style={{ maxHeight:'110px', maxWidth:'100%', objectFit:'contain', display:'block', margin:'0 auto' }} />
              </div>
            ) : (
              <div style={{ background:'#fff', border:'2px dashed #c0ccda', borderRadius:'7px', padding:'20px', textAlign:'center', marginBottom:'10px', color:'#adb5bd', fontSize:'11px' }}>
                Sin imagen cargada — escanea firma y sello juntos en una sola imagen
              </div>
            )}
            <button style={{ ...S.btnAzul, width:'100%', fontSize:'12px', padding:'8px' }}
              onClick={subirImagen} disabled={subiendoImg}>
              {subiendoImg ? '⏳ Subiendo...' : firmaSelloB64 ? '🔄 Cambiar imagen firma + sello' : '📷 Subir imagen firma + sello'}
            </button>
            {firmaSelloB64 && (
              <div style={{ fontSize:'10px', color:'#1D9E75', marginTop:'6px', textAlign:'center' }}>
                ✓ Imagen cargada — aparecerá automáticamente en notas clínicas
              </div>
            )}
          </div>
        )}

        {/* Contraseña — SOLO en edición (override por admin) */}
        {!esNuevo && (
          <>
            <div style={S.secH}>Cambiar contraseña (opcional — dejar vacío para no cambiar)</div>
            <div style={S.fila2}>
              <div style={S.campo}>
                <label style={S.label}>Nueva contraseña</label>
                <input style={S.input} type="password" value={form.password}
                  onChange={e => set('password', e.target.value)} placeholder="Mín. 6 caracteres" autoComplete="new-password" />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Confirmar</label>
                <input style={S.input} type="password" value={form.password2}
                  onChange={e => set('password2', e.target.value)} placeholder="Repetir" autoComplete="new-password" />
              </div>
            </div>
          </>
        )}

        <div style={{ display:'flex', gap:'10px', marginTop:'8px' }}>
          <button style={S.btnVerde} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : '✓ Guardar'}
          </button>
          <button style={S.btnS} onClick={onCerrar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  MODAL: Actividad del usuario
// ════════════════════════════════════════════════════════════
function ModalActividad({ usuarioVer, onCerrar }) {
  const [actividad, setActividad] = useState([]);
  const [cargando,  setCargando]  = useState(true);

  useEffect(() => {
    api.usuarios.actividad(usuarioVer.id, 50)
      .then(r => {
        if (r.ok) setActividad(r.data);
        setCargando(false);
      });
  }, [usuarioVer.id]);

  const iconoAccion = (accion) => {
    const map = { LOGIN:'🔑', LOGOUT:'🚪', INSERT:'➕', UPDATE:'✏️', DELETE:'🗑', ACCION:'⚙️' };
    return map[accion] || '•';
  };

  const colorAccion = (accion) => {
    const map = { LOGIN:'#1D9E75', LOGOUT:'#6c757d', INSERT:'#1B2A5E', UPDATE:'#e67e22', DELETE:'#dc3545' };
    return map[accion] || '#495057';
  };

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.modalCard, width:'600px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div>
            <div style={S.modalTitulo}>📋 Actividad de {usuarioVer.nombre}</div>
            <div style={{ fontSize:'12px', color:'#6c757d', marginTop:'-14px' }}>
              Últimas 50 acciones · Rol: {ROLES.find(r=>r.value===usuarioVer.rol)?.label || usuarioVer.rol}
            </div>
          </div>
          <button style={S.btnS} onClick={onCerrar}>✕ Cerrar</button>
        </div>

        {cargando ? (
          <div style={{ textAlign:'center', padding:'30px', color:'#6c757d' }}>Cargando actividad...</div>
        ) : actividad.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px', color:'#adb5bd', fontSize:'13px' }}>
            Sin actividad registrada para este usuario.
          </div>
        ) : (
          <div style={{ maxHeight:'500px', overflowY:'auto' }}>
            {actividad.map((a, i) => (
              <div key={i} style={S.actividadRow}>
                <span style={{ fontSize:'16px', flexShrink:0 }}>{iconoAccion(a.accion)}</span>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'2px' }}>
                    <span style={{ fontWeight:'700', color: colorAccion(a.accion), fontSize:'11px', textTransform:'uppercase' }}>{a.accion}</span>
                    {a.tabla && <span style={{ fontSize:'10px', color:'#adb5bd', background:'#f0f4f8', padding:'1px 6px', borderRadius:'4px' }}>{a.tabla}</span>}
                    {a.registro_id && <span style={{ fontSize:'10px', color:'#adb5bd' }}>#{a.registro_id}</span>}
                  </div>
                  {a.descripcion && <div style={{ fontSize:'12px', color:'#495057' }}>{a.descripcion}</div>}
                </div>
                <div style={{ fontSize:'10px', color:'#adb5bd', whiteSpace:'nowrap', flexShrink:0 }}>
                  {a.fecha_hora ? new Date(a.fecha_hora).toLocaleString('es-SV') : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Usuarios({ usuario }) {
  const [usuarios,    setUsuarios]    = useState([]);
  const [busqueda,    setBusqueda]    = useState('');
  const [filtroRol,   setFiltroRol]   = useState('Todos');
  const [filtroAct,   setFiltroAct]   = useState('activos');
  const [modal,       setModal]       = useState(null);   // null | 'nuevo' | usuario{}
  const [modalAct,    setModalAct]    = useState(null);   // usuario para ver actividad
  const [mensaje,     setMensaje]     = useState(null);
  const { confirmar, ModalConfirmarUI } = useConfirmar();

  const cargar = useCallback(async () => {
    const r = await api.usuarios.listar();
    if (r.ok) setUsuarios(r.data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const mostrarMsg = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje(null), 4000);
  };

  const toggleActivo = async (u) => {
    if (u.id === usuario.id) { mostrarMsg('warn', 'No puedes desactivar tu propia cuenta.'); return; }
    const nuevoEstado = u.activo ? 0 : 1;
    const accion = nuevoEstado ? 'activar' : 'desactivar';
    if (!(await confirmar({
      texto: `¿Deseas ${accion} al usuario "${u.nombre}"?`,
      tipo: 'advertencia',
      textoConfirmar: nuevoEstado ? 'Activar' : 'Desactivar',
    }))) return;
    const r = await api.usuarios.toggleActivo(u.id, nuevoEstado);
    if (r.ok) { cargar(); mostrarMsg('ok', `Usuario ${nuevoEstado ? 'activado' : 'desactivado'}.`); }
    else mostrarMsg('err', r.error);
  };

  const resetPassword = async (u) => {
    if (!(await confirmar({
      texto: `¿Resetear la contraseña de "${u.nombre}" a la contraseña inicial?\nEl usuario deberá cambiarla al próximo ingreso.`,
      tipo: 'peligro',
      textoConfirmar: 'Resetear contraseña',
    }))) return;
    const r = await api.usuarios.resetearPassword(u.id);
    if (r.ok) mostrarMsg('ok', `✓ Contraseña de "${u.nombre}" reseteada. Deberá cambiarla al ingresar.`);
    else mostrarMsg('err', r.error);
  };

  // Filtros
  const filtrados = usuarios.filter(u => {
    const txt = `${u.nombre} ${u.usuario} ${u.rol}`.toLowerCase();
    const porBus = !busqueda || txt.includes(busqueda.toLowerCase());
    const porRol = filtroRol === 'Todos' || u.rol === filtroRol;
    const porAct = filtroAct === 'todos' || (filtroAct === 'activos' ? u.activo : !u.activo);
    return porBus && porRol && porAct;
  });

  // KPIs
  const totalActivos   = usuarios.filter(u => u.activo).length;
  const totalInactivos = usuarios.filter(u => !u.activo).length;
  const porRolCount    = ROLES.map(r => ({ ...r, count: usuarios.filter(u => u.rol === r.value && u.activo).length })).filter(r => r.count > 0);

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {ModalConfirmarUI}
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          👥 Módulo de Usuarios
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)' }}>
          Cuentas del sistema · Roles · Permisos de acceso
        </div>
      </div>

      <div style={S.pagina}>
      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      {/* ── KPIs ── */}
      <div style={S.kpiGrid}>
        <div style={S.kpiCard}>
          <div style={S.kpiLabel}>Total usuarios</div>
          <div style={S.kpiVal}>{usuarios.length}</div>
          <div style={S.kpiSub}>registrados en el sistema</div>
        </div>
        <div style={S.kpiCard}>
          <div style={S.kpiLabel}>Activos</div>
          <div style={{ ...S.kpiVal, color: '#1D9E75' }}>{totalActivos}</div>
          <div style={S.kpiSub}>pueden iniciar sesión</div>
        </div>
        <div style={S.kpiCard}>
          <div style={S.kpiLabel}>Inactivos</div>
          <div style={{ ...S.kpiVal, color: '#dc3545' }}>{totalInactivos}</div>
          <div style={S.kpiSub}>acceso bloqueado</div>
        </div>
        <div style={{ ...S.kpiCard, gridColumn:'span 1' }}>
          <div style={S.kpiLabel}>Roles activos</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'4px', marginTop:'6px' }}>
            {porRolCount.map(r => (
              <span key={r.value} style={{ ...S.pillRol(r.value), fontSize:'10px' }}>
                {r.label} ({r.count})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tabla de usuarios ── */}
      <div style={S.card}>
        <div style={S.titulo}>
          <span>👥 Usuarios del sistema ({filtrados.length})</span>
          <button style={S.btnVerde} onClick={() => setModal('nuevo')}>+ Nuevo usuario</button>
        </div>

        {/* Filtros */}
        <div style={{ display:'flex', gap:'10px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
          <input style={{ ...S.input, maxWidth:'260px' }}
            placeholder="Buscar por nombre, usuario o rol..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />

          <select style={{ ...S.input, width:'auto' }}
            value={filtroRol} onChange={e => setFiltroRol(e.target.value)}>
            <option value="Todos">Todos los roles</option>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>

          {['activos','inactivos','todos'].map(f => (
            <button key={f} onClick={() => setFiltroAct(f)}
              style={{ padding:'6px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:'600', cursor:'pointer', border:'none',
                background: filtroAct === f ? '#1B2A5E' : '#e8f0fb',
                color:      filtroAct === f ? '#fff'    : '#1B2A5E' }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <table style={S.tabla}>
          <thead>
            <tr>
              {['','Usuario','DUI / Acceso','Rol','Estado','Acciones'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrados.map(u => (
              <tr key={u.id}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafd'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                {/* Avatar */}
                <td style={{ ...S.td, width:'50px' }}>
                  <div style={S.avatar(colorAvatar(u.rol))}>{iniciales(u.nombre)}</div>
                </td>

                {/* Nombre + usuario */}
                <td style={S.td}>
                  <div style={{ fontWeight:'700', color:'#1B2A5E' }}>
                    {u.nombre}
                    {u.id === usuario.id && (
                      <span style={{ marginLeft:'6px', fontSize:'10px', background:'#e8f0fb', color:'#1B2A5E', padding:'1px 6px', borderRadius:'4px', fontWeight:'600' }}>
                        Tú
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'2px' }}>#{u.id}</div>
                </td>

                {/* DUI / Usuario de login */}
                <td style={{ ...S.td, fontFamily:'monospace', fontSize:'12px', color:'#495057' }}>
                  <div>{u.usuario}</div>
                  {u.debe_cambiar_password ? (
                    <div style={{ fontSize:'10px', color:'#856404', background:'#fff3cd', borderRadius:'4px', padding:'1px 6px', display:'inline-block', marginTop:'3px', fontFamily:'Segoe UI, sans-serif' }}>
                      ⚠️ Debe cambiar contraseña
                    </div>
                  ) : null}
                </td>

                {/* Rol */}
                <td style={S.td}>
                  <span style={S.pillRol(u.rol)}>
                    {ROLES.find(r => r.value === u.rol)?.label || u.rol}
                  </span>
                  {(u.rol === 'medico' || u.rol === 'jefe_medico') && (
                    u.medico_id
                      ? <div style={{ fontSize:'10px', color:'#1D9E75', marginTop:'3px' }}>✓ Vinculado a Médicos</div>
                      : <div style={{ fontSize:'10px', color:'#856404', marginTop:'3px' }}>⚠ Sin vincular</div>
                  )}
                </td>

                {/* Estado */}
                <td style={S.td}>
                  <span style={S.pillActivo(u.activo)}>{u.activo ? 'Activo' : 'Inactivo'}</span>
                </td>

                {/* Acciones */}
                <td style={S.td}>
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                    <button style={S.btnAzul} onClick={() => setModal(u)} title="Editar usuario">
                      ✏️ Editar
                    </button>
                    <button style={S.btnNaranja} onClick={() => resetPassword(u)} title="Resetear contraseña">
                      🔑
                    </button>
                    <button style={S.btnGris} onClick={() => setModalAct(u)} title="Ver actividad">
                      📋
                    </button>
                    {u.id !== usuario.id && (
                      <button
                        style={u.activo ? S.btnRojo : S.btnVerde}
                        onClick={() => toggleActivo(u)}
                        title={u.activo ? 'Desactivar' : 'Activar'}>
                        {u.activo ? '🔒' : '🔓'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtrados.length === 0 && (
              <tr>
                <td colSpan="6" style={{ ...S.td, textAlign:'center', color:'#adb5bd', padding:'30px' }}>
                  No se encontraron usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Guía de roles ── */}
      <div style={S.card}>
        <div style={S.titulo}><span>🔐 Descripción de roles y accesos</span></div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px' }}>
          {ROLES.map(r => (
            <div key={r.value} style={{ padding:'10px 12px', borderRadius:'8px', background: r.bg, border:`1px solid ${r.color}33` }}>
              <div style={{ fontSize:'11px', fontWeight:'700', color: r.color, marginBottom:'4px' }}>{r.label}</div>
              <div style={{ fontSize:'10px', color:'#495057', lineHeight:'1.4' }}>{r.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modales ── */}
      {modal !== null && (
        <ModalUsuario
          usuario_actual={usuario}
          editar={modal === 'nuevo' ? null : modal}
          onGuardado={(msg) => { setModal(null); cargar(); mostrarMsg('ok', msg); }}
          onCerrar={() => setModal(null)}
          onFirmaSubida={async (userId, nuevaRuta) => {
            // Recargar lista de usuarios
            await cargar();
            // Actualizar el objeto modal con la nueva ruta para que el useEffect recargue la imagen
            setModal(prev => {
              if (!prev || prev === 'nuevo' || prev.id !== userId) return prev;
              return { ...prev, firma_sello_ruta: nuevaRuta };
            });
          }}
        />
      )}

      {modalAct && (
        <ModalActividad
          usuarioVer={modalAct}
          onCerrar={() => setModalAct(null)}
        />
      )}
      </div>
    </div>
  );
}
